import { createFileRoute, redirect, useRouter, useSearch } from "@tanstack/react-router";
import HelpCircle from "@repo/icons/default/HelpCircle";
import Left from "@repo/icons/default/Left";
import { z } from "zod";
import { graphql } from "@repo/graphql-types/gql";
import { useResetAtom } from "jotai/utils";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import { CreatePaymentForm } from "@/components/payment/forms/create-payment-form";
import { useGraphQL } from "@/hooks/use-graphql";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import { RecipientEmptyStateWrapper } from "@/components/payment/bank/account/recipient-empty-state";
import { CreatePaymentTooltipDrawer } from "@/components/payment/forms/create-payment-tooltip-drawer";
import { createPaymentFormAtom } from "@/lib/atoms/create-payment-atom";
import { RecipientInvalidAlertWrapper } from "@/components/payment/bank/account/recipient-invalid-alert-wrapper";
import { hasUserAcceptedOnlinePaymentTermsOfUse } from "@/lib/user-accepted-online-payment-terms-of-use";

import type { JSX } from "react";

const CreatePaymentPageQuery = graphql(/* GraphQL */ `
  query CreatePaymentPageQuery($appointmentId: Int!, $patientId: Int!) {
    ...CreatePaymentFormFragment
  }
`);

const CreatePaymentPage = (): JSX.Element => {
  const router = useRouter();
  const searchParams = useSearch({ from: "/settings/payments/create/" });
  const { appointmentId, patientId, about } = searchParams;

  const queryResult = useGraphQL(CreatePaymentPageQuery, {
    appointmentId: appointmentId ?? 0,
    patientId: patientId ?? 0,
  });

  const { data } = queryResult;

  const resetCreatePaymentForm = useResetAtom(createPaymentFormAtom);

  const backNavigationMap: Record<string, () => void> = {
    "appointment-created": () => {
      void router.navigate({ to: "/calendar" });
    },
    "terms-of-service": () => {
      router.history.go(-2);
    },
    default: () => {
      router.history.back();
    },
  };

  const onBackButtonClick = (): void => {
    resetCreatePaymentForm();
    backNavigationMap[searchParams.from ?? "default"]();
  };

  const setOpenTooltipDrawer = (open: boolean): void => {
    if (open) {
      void router.navigate({
        to: ".",
        search: {
          ...searchParams,
          about: true,
        },
      });
    } else if (about) {
      router.history.back();
    }
  };

  return (
    <>
      <HeaderRoot>
        <HeaderButton align="start" icon={Left} onClick={onBackButtonClick} />
        <HeaderTitle title="Criar pagamento" align="center" />
        {!appointmentId ? (
          <HeaderButton
            icon={HelpCircle}
            onClick={() => setOpenTooltipDrawer(true)}
            align="end"
            data-testid="create-payment-help-button"
          />
        ) : (
          // eslint-disable-next-line react/jsx-no-useless-fragment --- não da pra passar null dentro do header
          // biome-ignore lint/complexity/noUselessFragments: legacy code
          <></>
        )}
      </HeaderRoot>
      <Page>
        <RecipientEmptyStateWrapper>
          <RecipientInvalidAlertWrapper>
            <AsyncDataWrapper {...queryResult}>
              {data ? <CreatePaymentForm data={data} /> : null}
            </AsyncDataWrapper>
          </RecipientInvalidAlertWrapper>
        </RecipientEmptyStateWrapper>
        {!appointmentId ? (
          <CreatePaymentTooltipDrawer
            open={Boolean(about)}
            setOpen={setOpenTooltipDrawer}
          />
        ) : null}
      </Page>
    </>
  );
};

export const Route = createFileRoute("/settings/payments/create/")({
  component: CreatePaymentPage,
  validateSearch: z.object({
    appointmentId: z.number().optional(),
    patientId: z.number().optional(),
    origin: z.string().optional(),
    about: z.boolean().optional(),
    from: z.string().optional(),
  }),
  beforeLoad: async ({ context, search }) => {
    const hasUserAccepted = await hasUserAcceptedOnlinePaymentTermsOfUse(context);
    if (!hasUserAccepted) {
      redirect({ to: "/settings/payments/terms-of-service", throw: true, search });
    }
  },
});
